import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { handleUnauthorized } from '@/lib/api-error-handler'
import { BACKEND_URL } from '@/lib/config'
import { getAuthToken } from '@/lib/supabase'

const SETTING_KEY = 'snapshots_enabled'

interface SnapshotsEnabledContextValue {
  snapshotsEnabled: boolean
  isLoading: boolean
  setSnapshotsEnabled: (value: boolean) => Promise<void>
}

const SnapshotsEnabledContext = createContext<SnapshotsEnabledContextValue>({
  snapshotsEnabled: false,
  isLoading: true,
  setSnapshotsEnabled: async () => {},
})

export function SnapshotsEnabledProvider({
  children,
}: { children: ReactNode }) {
  const [snapshotsEnabled, setSnapshotsEnabledState] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function fetchSetting() {
      const token = await getAuthToken()
      if (!token) {
        setIsLoading(false)
        return
      }

      try {
        const response = await fetch(
          `${BACKEND_URL}/api/settings/${SETTING_KEY}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'ngrok-skip-browser-warning': 'true',
            },
          }
        )

        if (!response.ok) {
          if (response.status === 401) {
            await handleUnauthorized(response, 'Fetch snapshots setting')
          }
          return
        }

        const data = await response.json()
        if (!cancelled) {
          setSnapshotsEnabledState(data.value === true)
        }
      } catch (error) {
        console.error('Failed to fetch snapshots setting:', error)
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchSetting()
    return () => {
      cancelled = true
    }
  }, [])

  const setSnapshotsEnabled = useCallback(async (value: boolean) => {
    // Optimistic update
    setSnapshotsEnabledState(value)

    const token = await getAuthToken()
    if (!token) return

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/settings/${SETTING_KEY}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({ value }),
        }
      )

      if (!response.ok) {
        if (response.status === 401) {
          await handleUnauthorized(response, 'Update snapshots setting')
          return
        }
        // Revert on failure
        setSnapshotsEnabledState(!value)
        console.error('Failed to update snapshots setting')
      }
    } catch (error) {
      // Revert on failure
      setSnapshotsEnabledState(!value)
      console.error('Failed to update snapshots setting:', error)
    }
  }, [])

  return (
    <SnapshotsEnabledContext.Provider
      value={{ snapshotsEnabled, isLoading, setSnapshotsEnabled }}
    >
      {children}
    </SnapshotsEnabledContext.Provider>
  )
}

export function useSnapshotsEnabled() {
  return useContext(SnapshotsEnabledContext)
}
